/**
 * Map a cron expression to one of the five Schedule-form presets.
 * Returns null if the expression doesn't fit any preset shape.
 *
 * Presets are: hourly, daily, weekly, monthly. (Off is represented by
 * `enabled = false` on the schedule, not by a cron value.)
 *
 * Daily carries the weekdays it runs on (#2191): a day-of-week list or range
 * such as `2-6` or `1,3,5` is Daily on those days, and `*` is all seven. A
 * single day stays Weekly — Daily with one day ticked emits that same cron.
 */
import { parseDayOfWeekField } from '@/lib/cron/dayOfWeek';

export type SchedulePreset =
  | { frequency: 'hourly'; minute: number }
  | { frequency: 'daily';   hour: number; minute: number; ampm: 'AM' | 'PM'; days: number[] }
  | { frequency: 'weekly';  hour: number; minute: number; ampm: 'AM' | 'PM'; day: number }
  | { frequency: 'monthly'; hour: number; minute: number; ampm: 'AM' | 'PM'; day: number };

const STAR = '*';
const VALID_MINUTES = new Set<number>([0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]);

function toAmPm(hour24: number): { hour: number; ampm: 'AM' | 'PM' } {
  if (hour24 === 0)  return { hour: 12, ampm: 'AM' };
  if (hour24 === 12) return { hour: 12, ampm: 'PM' };
  if (hour24 > 12)   return { hour: hour24 - 12, ampm: 'PM' };
  return { hour: hour24, ampm: 'AM' };
}

function parseInt0to59(s: string): number | null {
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n >= 0 && n <= 59 ? n : null;
}

function parseInt0to23(s: string): number | null {
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n >= 0 && n <= 23 ? n : null;
}

function parseInt1to28(s: string): number | null {
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n >= 1 && n <= 28 ? n : null;
}

export function cronExpressionToPreset(cron: string | null | undefined): SchedulePreset | null {
  if (!cron || typeof cron !== 'string') return null;
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [min, hour, dom, mon, dow] = parts;

  // Month must always be * for any preset.
  if (mon !== STAR) return null;

  // Hourly: minute concrete, hour/dom/dow all *.
  if (hour === STAR && dom === STAR && dow === STAR) {
    const m = parseInt0to59(min);
    if (m === null || !VALID_MINUTES.has(m)) return null;
    return { frequency: 'hourly', minute: m };
  }

  // Concrete minute for the remaining presets.
  const m = parseInt0to59(min);
  if (m === null || !VALID_MINUTES.has(m)) return null;
  const h = parseInt0to23(hour);
  if (h === null) return null;
  const { hour: hour12, ampm } = toAmPm(h);

  // Daily (dow is * or a set of 2+ days) or Weekly (exactly one day); dom is *.
  if (dom === STAR) {
    const days = parseDayOfWeekField(dow);
    if (days === null) return null;
    if (days.length === 1) {
      return { frequency: 'weekly', hour: hour12, minute: m, ampm, day: days[0] };
    }
    return { frequency: 'daily', hour: hour12, minute: m, ampm, days };
  }

  // Monthly: dom is concrete 1–28, dow is *.
  if (dom !== STAR && dow === STAR) {
    const d = parseInt1to28(dom);
    if (d === null) return null;
    return { frequency: 'monthly', hour: hour12, minute: m, ampm, day: d };
  }

  return null;
}
